import {
  useAdminCatalogSearch,
  useAdminCatalogSong,
  useClearAllCatalogStats,
  type AdminCatalogSong,
} from '@church/data'
import { THEMES, type CatalogThemeSource } from '@church/domain'
import { faBan, faEraser, faMagnifyingGlass, faRotateLeft, faThumbtack } from '@church/ui-icons'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Artwork } from '../components/services/AppleMusic.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Icon,
  IconButton,
  Input,
  ListTitle,
  PageHeader,
  PanelSection,
  Select,
  SidePanel,
  Spinner,
  TableScroll,
  listRow,
} from '../components/ui.tsx'

/**
 * The shared song catalog in the platform console (docs/15-MUSIC-AND-AI.md,
 * "Song themes from the shared catalog"): search its recordings; each row
 * reads how many churches have it and its themes; choosing one opens its
 * panel — its statistics (in how many libraries, its Sundays, what churches
 * sang it for), its themes with where each came from, a pin or block on any
 * theme (an operator's word over the churches'), and Clear its stats in the
 * footer. "Clear all song stats" wipes usage and theme choices across the
 * catalog, instead of reseeding: churches' songs and their themes stay.
 */
export const Route = createFileRoute('/_authed/admin/catalog')({
  component: AdminCatalog,
})

const SOURCE_TONE: Record<
  CatalogThemeSource,
  'brand' | 'success' | 'warning' | 'danger' | 'violet'
> = {
  ai: 'brand',
  churches: 'success',
  dropped: 'warning',
  pinned: 'violet',
  blocked: 'danger',
}

const shortDay = (iso: string) =>
  new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })

function AdminCatalog() {
  const search = useAdminCatalogSearch()
  const clearAll = useClearAllCatalogStats()
  const [openId, setOpenId] = useState<string | null>(null)
  const list = search.list

  return (
    <div className="space-y-6">
      <PageHeader visible title="Catalog" />
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Icon
            icon={faMagnifyingGlass}
            className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-content-subtle"
          />
          <Input
            value={search.q}
            onChange={(event) => search.setQ(event.target.value)}
            placeholder="Search by title or artist"
            aria-label="Search the catalog"
            className="pl-9"
          />
        </div>
        {/* On a phone the search gets the row; the clearing is its icon (the same question asks). */}
        {[true, false].map((phone) => (
          <span key={String(phone)} className={phone ? 'sm:hidden' : 'hidden sm:block'}>
            <ConfirmButton
              label="Clear all song stats"
              icon={faEraser}
              iconOnly={phone}
              variant="secondary"
              title="Clear every song’s statistics?"
              description="How often each recording is sung and what churches sang it for are wiped across the catalog, and count again from services after today. Churches’ songs and their themes stay as they are."
              confirmLabel="Clear all"
              pendingLabel="Clearing…"
              onConfirm={() => clearAll.mutateAsync()}
            />
          </span>
        ))}
      </div>

      <Card bleed>
        <ListTitle title="Recordings" />
        {list.isPending ? (
          <div className="p-4">
            <Spinner />
          </div>
        ) : list.error ? (
          <div className="p-4">
            <ErrorNote error={list.error} />
          </div>
        ) : search.rows.length === 0 ? (
          <EmptyState
            title={search.q ? 'Nothing in the catalog by that name' : 'The catalog is empty'}
          />
        ) : (
          <ul className="divide-y divide-border-subtle">
            {search.rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(row.id)}
                  className={`${listRow} flex w-full items-center gap-3 text-left hover:bg-surface-sunken`}
                >
                  <Artwork template={row.artworkUrl} background={row.bgColor} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base text-content-default">
                      {row.title}
                    </span>
                    <span className="block truncate text-sm text-content-muted">
                      {[row.artist, row.album].filter(Boolean).join(' · ')}
                    </span>
                    <span className="block truncate text-sm text-content-muted">
                      {row.themes.length > 0
                        ? row.themes.join(', ')
                        : row.themesAt
                          ? 'No themes'
                          : 'Themes not given yet'}
                    </span>
                  </span>
                  <Chip size="md" tone={row.libraryChurches > 0 ? 'brand' : 'neutral'}>
                    <span className="tabular-nums">{row.libraryChurches}</span>
                    <span className="sr-only">
                      {row.libraryChurches === 1 ? 'church has it' : 'churches have it'}
                    </span>
                  </Chip>
                </button>
              </li>
            ))}
          </ul>
        )}
        {list.hasNextPage && (
          <div className="p-3">
            <Button
              variant="ghost"
              pending={list.isFetchingNextPage}
              pendingLabel="Loading…"
              onClick={() => void list.fetchNextPage()}
            >
              More
            </Button>
          </div>
        )}
      </Card>
      <p className="text-sm text-content-muted">
        The number on each row is how many churches have the recording in their library. Its themes
        are the AI’s, plus what at least three churches added, less what at least three removed when
        they are half of the churches that have it. A pin or block here overrides them.
      </p>
      {openId && <RecordingPanel id={openId} onClose={() => setOpenId(null)} />}
    </div>
  )
}

/** A recording's panel: its statistics, its themes and where each came from, and clearing. */
function RecordingPanel({ id, onClose }: { id: string; onClose: () => void }) {
  const { song, setTheme, clear } = useAdminCatalogSong(id)
  const data = song.data
  return (
    <SidePanel
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={data?.recording.name ?? 'Recording'}
      description={
        data
          ? [data.recording.artistName, data.recording.albumName, data.storefront.toUpperCase()]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
      footer={
        data ? (
          <ConfirmButton
            label="Clear its stats"
            icon={faEraser}
            variant="quietDanger"
            className="-ml-4"
            title={`Clear the statistics of ${data.recording.name}?`}
            description="Its Sundays and what churches sang it for are wiped, and count again from services after today. Churches’ songs and their themes stay as they are."
            confirmLabel="Clear"
            pendingLabel="Clearing…"
            onConfirm={() => clear.mutateAsync()}
          />
        ) : undefined
      }
    >
      {song.isPending ? (
        <Spinner />
      ) : !data ? (
        <ErrorNote error={song.error} />
      ) : (
        <div className="space-y-6">
          <ErrorNote error={setTheme.error ?? clear.error} />
          <div className="flex items-center gap-3">
            <Artwork
              template={data.recording.artwork?.url ?? null}
              background={data.recording.artwork?.bgColor}
              size="md"
            />
            <dl className="min-w-0 flex-1 space-y-0.5 text-sm">
              <div className="flex gap-1.5">
                <dt className="text-content-muted">In libraries</dt>
                <dd className="text-content-default tabular-nums">
                  {data.libraryChurches} {data.libraryChurches === 1 ? 'church' : 'churches'}
                </dd>
              </div>
              <div className="flex gap-1.5">
                <dt className="text-content-muted">Themes</dt>
                <dd className="truncate text-content-default">
                  {data.themesAt
                    ? `by AI, ${shortDay(data.themesAt)}${data.themesModel ? ` · ${data.themesModel}` : ''}`
                    : 'not given yet'}
                </dd>
              </div>
              {data.clearedOn && (
                <div className="flex gap-1.5">
                  <dt className="text-content-muted">Stats cleared</dt>
                  <dd className="text-content-default">{shortDay(data.clearedOn)}</dd>
                </div>
              )}
            </dl>
          </div>
          <ThemesSection data={data} setTheme={setTheme} />
          <UsageSection data={data} />
          <SungForSection data={data} />
        </div>
      )}
    </SidePanel>
  )
}

type SetTheme = ReturnType<typeof useAdminCatalogSong>['setTheme']

function ThemesSection({ data, setTheme }: { data: AdminCatalogSong; setTheme: SetTheme }) {
  const [adding, setAdding] = useState('')
  const listed = new Set(data.themes.map((t) => t.theme))
  return (
    <PanelSection
      title="Its themes"
      hint="Pinned: every church linking it wears it. Blocked: none does from the catalog. Churches’ own choices on their songs stay theirs."
    >
      {data.themes.length === 0 ? (
        <p className="text-sm text-content-muted">None yet.</p>
      ) : (
        <ul className="-mx-panel-x divide-y divide-border-subtle">
          {data.themes.map((theme) => (
            <li key={theme.theme} className={`${listRow} flex items-center gap-2 px-panel-x`}>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                  <span
                    className={`truncate text-base ${theme.active ? 'text-content-default' : 'text-content-subtle line-through'}`}
                  >
                    {theme.label}
                  </span>
                  {theme.source && theme.sourceLabel && (
                    <Chip tone={SOURCE_TONE[theme.source]}>{theme.sourceLabel}</Chip>
                  )}
                </span>
                <span className="block truncate text-sm text-content-muted tabular-nums">
                  Added by {theme.addedChurches} · removed by {theme.removedChurches}
                </span>
              </span>
              {theme.operator ? (
                <IconButton
                  icon={faRotateLeft}
                  label={`Leave ${theme.label} to the churches`}
                  disabled={setTheme.isPending}
                  onClick={() => setTheme.mutate({ theme: theme.theme, operator: null })}
                />
              ) : (
                <>
                  <IconButton
                    icon={faThumbtack}
                    label={`Pin ${theme.label}`}
                    disabled={setTheme.isPending}
                    onClick={() => setTheme.mutate({ theme: theme.theme, operator: 'pinned' })}
                  />
                  <IconButton
                    icon={faBan}
                    label={`Block ${theme.label}`}
                    disabled={setTheme.isPending}
                    onClick={() => setTheme.mutate({ theme: theme.theme, operator: 'blocked' })}
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <Select
          value={adding}
          aria-label="A theme to pin"
          onChange={(event) => setAdding(event.target.value)}
        >
          <option value="">Pin another theme…</option>
          {THEMES.filter((t) => !listed.has(t.slug)).map((t) => (
            <option key={t.slug} value={t.slug}>
              {t.label}
            </option>
          ))}
        </Select>
        <Button
          variant="secondary"
          icon={faThumbtack}
          disabled={!adding}
          pending={setTheme.isPending}
          onClick={() =>
            setTheme.mutate(
              { theme: adding, operator: 'pinned' },
              { onSuccess: () => setAdding('') },
            )
          }
        >
          Pin
        </Button>
      </div>
    </PanelSection>
  )
}

function UsageSection({ data }: { data: AdminCatalogSong }) {
  return (
    <PanelSection title="Sundays">
      {data.usage.length === 0 ? (
        <p className="text-sm text-content-muted">Not on any plan in the weeks counted.</p>
      ) : (
        <TableScroll className="-mx-panel-x">
          <table className="w-full text-sm">
            <thead className="bg-surface-heading text-left text-xs tracking-wide text-content-muted uppercase">
              <tr>
                <th className="px-panel-x py-2 font-medium">Week of</th>
                <th className="px-2 py-2 text-right font-medium">Churches</th>
                <th className="px-2 py-2 text-right font-medium">Plans</th>
                <th className="px-panel-x py-2 text-right font-medium">4 weeks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {data.usage.map((week) => (
                <tr key={week.weekStart}>
                  <td className="px-panel-x py-2 text-content-default">
                    {shortDay(week.weekStart)}
                  </td>
                  <td className="px-2 py-2 text-right text-content-muted tabular-nums">
                    {week.churches}
                  </td>
                  <td className="px-2 py-2 text-right text-content-muted tabular-nums">
                    {week.plans}
                  </td>
                  <td className="px-panel-x py-2 text-right text-content-muted tabular-nums">
                    {week.churchesMonth}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}
    </PanelSection>
  )
}

function SungForSection({ data }: { data: AdminCatalogSong }) {
  return (
    <PanelSection title="Sung for">
      {data.themeStats.length === 0 ? (
        <p className="text-sm text-content-muted">No sermon themes counted for it.</p>
      ) : (
        <ul className="-mx-panel-x divide-y divide-border-subtle">
          {data.themeStats.map((stat) => (
            <li key={stat.theme} className={`${listRow} px-panel-x`}>
              <span className="block text-base text-content-default">{stat.label}</span>
              <span className="block text-sm text-content-muted tabular-nums">
                {stat.churches} {stat.churches === 1 ? 'church' : 'churches'} · {stat.plans}{' '}
                {stat.plans === 1 ? 'plan' : 'plans'} · suggested {stat.suggestedCount}, added{' '}
                {stat.addedCount}
                {stat.lastSungOn ? ` · last ${shortDay(stat.lastSungOn)}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </PanelSection>
  )
}
